"use client";

import * as React from "react";
import {
  Button,
  Drawer,
  CloseButton,
  Portal,
  HStack,
  VStack,
  Text,
  Flex,
} from "@chakra-ui/react";
import { Checkbox } from "./checkbox";
import { LuLibraryBig } from "react-icons/lu";

export interface TableOption {
  label: string;
  value: string;
}

export interface FolderOption {
  label: string;
  value: string;
}

export interface TableSelectDrawerProps {
  tables?: TableOption[];
  folders?: FolderOption[];
  onSelect: (selectedTables: string[], selectedFolders: string[]) => void;
}

export const TableSelectDrawer = ({
  tables = [],
  folders = [],
  onSelect,
}: TableSelectDrawerProps) => {
  const [selectedTables, setSelectedTables] = React.useState<string[]>([]);
  const [selectedFolders, setSelectedFolders] = React.useState<string[]>([]);

  const allTablesSelected =
    tables.length > 0 && tables.every((t) => selectedTables.includes(t.value));
  const allFoldersSelected =
    folders.length > 0 &&
    folders.every((f) => selectedFolders.includes(f.value));

  // 초기화 로직 개선: 컴포넌트 마운트 시에만 실행
  const initialized = React.useRef(false);

  React.useEffect(() => {
    if (!initialized.current) {
      // 초기 로드 시 모든 항목 선택
      if (tables.length > 0) {
        setSelectedTables(tables.map((t) => t.value));
      }
      if (folders.length > 0) {
        setSelectedFolders(folders.map((f) => f.value));
      }
      initialized.current = true;
    }
  }, [tables, folders]);

  // props 변경 시 선택 상태 유지하면서 새로운 항목만 추가
  React.useEffect(() => {
    if (initialized.current) {
      setSelectedTables((prev) => {
        const newTables = tables.map((t) => t.value);
        const existingTables = prev.filter((t) => newTables.includes(t));
        return existingTables.length > 0 ? existingTables : newTables;
      });

      setSelectedFolders((prev) => {
        const newFolders = folders.map((f) => f.value);
        const existingFolders = prev.filter((f) => newFolders.includes(f));
        return existingFolders.length > 0 ? existingFolders : newFolders;
      });
    }
  }, [tables, folders]);

  const toggleItem = (
    value: string,
    setSelected: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setSelected((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const toggleAll = (
    items: { value: string }[],
    setSelected: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    setSelected((prev) => {
      const allSelected = items.every((item) => prev.includes(item.value));
      return allSelected ? [] : items.map((i) => i.value);
    });
  };

  const arraysEqual = React.useCallback(
    (a: string[], b: string[]) =>
      a.length === b.length && a.every((val, idx) => val === b[idx]),
    []
  );

  const prevSelectedRef = React.useRef({
    tables: [] as string[],
    folders: [] as string[],
  });

  React.useEffect(() => {
    const prev = prevSelectedRef.current;
    const tablesChanged = !arraysEqual(prev.tables, selectedTables);
    const foldersChanged = !arraysEqual(prev.folders, selectedFolders);

    if (tablesChanged || foldersChanged) {
      onSelect(selectedTables, selectedFolders);
      prevSelectedRef.current = {
        tables: selectedTables,
        folders: selectedFolders,
      };
    }
  }, [selectedTables, selectedFolders, onSelect, arraysEqual]);

  return (
    <Drawer.Root placement="bottom">
      <Drawer.Trigger asChild>
        <Button
          width="90px"
          variant="ghost"
          borderRadius="2xl"
          border="1px solid #e2e8f0"
          size="sm"
          px={3}
        >
          <Flex align="center" gap={1}>
            <LuLibraryBig />
            <Text>지식 자산</Text>
          </Flex>
        </Button>
      </Drawer.Trigger>

      <Portal>
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content roundedTop="xl">
            <Drawer.Header mt={8}>
              <Drawer.Title>테이블 및 문서 선택</Drawer.Title>
            </Drawer.Header>

            <Drawer.Body>
              {/* 테이블 선택 */}
              <VStack align="stretch" gap={2} mb={8}>
                <Text fontWeight="bold" fontSize="sm">
                  테이블
                </Text>
                <Button
                  size="xs"
                  variant="outline"
                  onClick={() => toggleAll(tables, setSelectedTables)}
                >
                  {allTablesSelected ? "전체 해제" : "전체 선택"}
                </Button>
                {tables.map((table) => (
                  <HStack key={table.value} justify="space-between" w="100%">
                    <Text fontSize="sm">{table.label}</Text>
                    <Checkbox
                      checked={selectedTables.includes(table.value)}
                      inputProps={{
                        onChange: () =>
                          toggleItem(table.value, setSelectedTables),
                      }}
                    />
                  </HStack>
                ))}
              </VStack>

              {/* 문서 선택 */}
              {folders.length > 0 && (
                <VStack align="stretch" gap={2} mb={10}>
                  <Text fontWeight="bold" fontSize="sm">
                    문서
                  </Text>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => toggleAll(folders, setSelectedFolders)}
                  >
                    {allFoldersSelected ? "전체 해제" : "전체 선택"}
                  </Button>
                  {folders.map((folder) => (
                    <HStack key={folder.value} justify="space-between" w="100%">
                      <Text fontSize="sm">{folder.label}</Text>
                      <Checkbox
                        checked={selectedFolders.includes(folder.value)}
                        inputProps={{
                          onChange: () =>
                            toggleItem(folder.value, setSelectedFolders),
                        }}
                      />
                    </HStack>
                  ))}
                </VStack>
              )}
            </Drawer.Body>

            {/* <Drawer.Footer>
              <Drawer.ActionTrigger asChild>
                <Button variant="outline">취소</Button>
              </Drawer.ActionTrigger>
              <Button onClick={() => {}}>확인</Button>
            </Drawer.Footer> */}

            <Drawer.CloseTrigger asChild>
              <CloseButton size="sm" />
            </Drawer.CloseTrigger>
          </Drawer.Content>
        </Drawer.Positioner>
      </Portal>
    </Drawer.Root>
  );
};
